import Swiper from 'swiper';
import { Autoplay, EffectFade, Navigation, Pagination, Parallax } from 'swiper/modules';
import type { NavigationOptions, PaginationOptions, SwiperEvents, SwiperOptions } from 'swiper/types';

export class SwiperService {
  container: HTMLElement;
  wrapper: HTMLElement;
  instance: Swiper;

  constructor(container: HTMLElement, wrapper: HTMLElement) {
    this.container = container;
    this.wrapper = wrapper;
  }

  init(options?: SwiperOptions) {
    const swiperOptions: SwiperOptions = {
      /**
       * CSS class modifiers so the carousel CSS don't
       * clash with integration CSS classes
       */
      containerModifierClass: 'pk-carousel--container__',
      effect: options.effect,
      slideClass: 'pk-carousel--slide',
      slideActiveClass: 'pk-carousel--slide-active',
      slideNextClass: 'pk-carousel--slide-next',
      slidePrevClass: 'pk-carousel--slide-prev',
      wrapperClass: 'pk-carousel--container__wrapper',
      ...options,
      navigation: {
        disabledClass: 'pk-carousel--navigation-disabled',
        hiddenClass: 'pk-carousel--navigation-hidden',
        lockClass: 'pk-carousel--navigation-lock',
        navigationDisabledClass: 'pk-carousel--navigation-disabled',
        ...(options?.navigation as NavigationOptions),
      },
      pagination: {
        modifierClass: 'pk-carousel__pagination--',
        bulletClass: 'pk-carousel__pagination--bullet sc-pk-carousel',
        bulletActiveClass: 'pk-carousel__pagination--bullet-active',
        clickableClass: 'pk-carousel__pagination--clickable',
        horizontalClass: 'pk-carousel__pagination--horizontal',
        hiddenClass: 'pk-carousel__pagination--hidden',
        lockClass: 'pk-carousel__pagination--lock',
        ...(options?.pagination as PaginationOptions),
      },
      watchSlidesProgress: true,
    };

    const slideElements = Array.from(this.wrapper.children);
    for (const slideElem of slideElements) {
      slideElem.classList.add('pk-carousel--slide');
    }

    if (this.instance) {
      this.instance.destroy();
    }

    const { autoplay, loop, effect, pagination, parallax } = swiperOptions;

    const crossFade = effect === 'fade';
    this.instance = new Swiper(this.container, {
      ...swiperOptions,
      fadeEffect: { crossFade },
      modules: [
        autoplay && Autoplay,
        crossFade && EffectFade,
        Navigation,
        pagination && Pagination,
        parallax && Parallax,
      ].filter(Boolean),
      on: {
        // Event will be fired when Swiper progress is changed
        progress: (swiper: Swiper) => {
          if (!swiper?.navigation) return;

          const prevEl = swiper.navigation.prevEl as HTMLPkButtonElement;
          if (prevEl) prevEl.disabled = !loop && swiper.isBeginning;
          const nextEl = swiper.navigation.nextEl as HTMLPkButtonElement;
          if (nextEl) nextEl.disabled = !loop && swiper.isEnd;
        },
      },
    });
  }

  update() {
    this.instance?.update();
  }

  destroy() {
    this.instance?.destroy();
  }

  onResize(callback: SwiperEvents['resize']) {
    this.instance?.on('resize', callback);
  }

  onTransitionEnd(callback: SwiperEvents['transitionEnd']) {
    this.instance?.on('transitionEnd', callback);
  }

  onTransitionStart(callback: SwiperEvents['transitionStart']) {
    this.instance?.on('transitionStart', callback);
  }

  onTouchSlide(callback: SwiperEvents['touchEnd']) {
    this.instance?.on('touchEnd', callback);
  }

  onSlideChange(callback: SwiperEvents['slideChange']) {
    this.instance?.on('slideChange', callback);
  }

  onSlideMove(callback: SwiperEvents['sliderMove']) {
    this.instance?.on('sliderMove', callback);
  }

  onSetTranslate(callback: SwiperEvents['setTranslate']) {
    this.instance?.on('setTranslate', callback);
  }
}
